/**
 * AI Prompt Definitions — Single Source of Truth
 *
 * All AI prompts used across the application are defined here.
 * Import from this file instead of defining prompts inline.
 *
 * Consumed by:
 *   - src/contexts/PromptContext.jsx (fallback when Firestore is empty)
 *   - scripts/seedAdminData.js (seeds Firestore)
 *   - src/utils/openai.js (legacy fallback prompts)
 */

export const PROMPT_DEFINITIONS = {
  generateWOBriefing: {
    id: 'generateWOBriefing',
    name: 'Work Order Briefing Generator',
    product: 'trades-app',
    currentVersion: 1,
    systemPrompt: 'You are a helpful maintenance assistant. Be concise and practical.',
    userPromptTemplate: `You are a helpful assistant for a maintenance technician.

Current Work Order:
- ID: {{woId}}
- Issue: {{description}}
- Location: {{property}} Unit {{unit}}

Unit History (past 12 months):
{{historyText}}

{{notesText}}

Provide a brief for the tech:
1. Summarize the current issue
2. Highlight any patterns from history
3. Note which techs worked here before and relevant findings
4. Give 1-2 actionable recommendations

Keep it concise and practical.`,
    templateVariables: [
      { name: 'woId', type: 'string', description: 'Work order ID' },
      { name: 'description', type: 'string', description: 'Issue description' },
      { name: 'property', type: 'string', description: 'Property name' },
      { name: 'unit', type: 'string', description: 'Unit number' },
      { name: 'historyText', type: 'string', description: 'Formatted unit history' },
      { name: 'notesText', type: 'string', description: 'Formatted labor notes' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 1000,
    temperature: 0.7,
    outputFormat: 'text',
    jsonSchema: null,
    status: 'active',
  },

  analyzePhoto: {
    id: 'analyzePhoto',
    name: 'Photo Analysis',
    product: 'trades-app',
    currentVersion: 1,
    systemPrompt: 'You are helping a maintenance tech diagnose an issue. Be practical and specific.',
    userPromptTemplate: `Context: {{context}}

What do you see in this image? What might be the problem and potential solutions?`,
    templateVariables: [
      { name: 'context', type: 'string', description: 'Context about the maintenance issue' },
    ],
    model: 'gpt-4o',
    maxTokens: 500,
    temperature: 0.7,
    outputFormat: 'text',
    jsonSchema: null,
    status: 'active',
  },

  generateDebugChecklist: {
    id: 'generateDebugChecklist',
    name: 'Debug Checklist Generator',
    product: 'trades-app',
    currentVersion: 1,
    systemPrompt: 'You are a maintenance expert. Be practical and specific with prices.',
    userPromptTemplate: `Generate a practical debug checklist for a maintenance tech dealing with: {{issueDescription}}
{{historyContext}}

Include:
1. What to check first (3-5 items)
2. Likely causes based on common issues
3. Potential solutions
4. Parts that might be needed (with rough price estimates)

Format as a clear, actionable checklist.`,
    templateVariables: [
      { name: 'issueDescription', type: 'string', description: 'Description of the issue' },
      { name: 'historyContext', type: 'string', description: 'Formatted unit history' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 1000,
    temperature: 0.7,
    outputFormat: 'text',
    jsonSchema: null,
    status: 'active',
  },

  analyzeWOEfficiency: {
    id: 'analyzeWOEfficiency',
    name: 'Work Order Efficiency Analyzer',
    product: 'trades-app',
    currentVersion: 1,
    systemPrompt: 'You are a maintenance efficiency analyst. Respond only with valid JSON.',
    userPromptTemplate: `You are analyzing maintenance work orders for efficiency.

Work Order: {{description}}
Hours Logged: {{hours}}
Parts Used: {{partsText}}
Unit History: {{historyText}}

Does this seem like an above-average use of time? Consider:
- Simple tasks (faucet replacement, unclog drain) should be 0.5-2 hours
- Complex tasks (water heater, HVAC repair) may be 2-4+ hours
- No parts on a repair job is unusual

Respond with JSON only (no markdown):
{
  "flagged": true or false,
  "reason": "Brief explanation",
  "confidence": "high", "medium", or "low"
}`,
    templateVariables: [
      { name: 'description', type: 'string', description: 'Work order description' },
      { name: 'hours', type: 'number', description: 'Hours logged' },
      { name: 'partsText', type: 'string', description: 'Formatted parts list' },
      { name: 'historyText', type: 'string', description: 'Formatted unit history' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 200,
    temperature: 0.7,
    outputFormat: 'json',
    jsonSchema: {
      type: 'object',
      properties: {
        flagged: { type: 'boolean' },
        reason: { type: 'string' },
        confidence: { type: 'string', enum: ['high', 'medium', 'low'] },
      },
      required: ['flagged', 'reason', 'confidence'],
    },
    status: 'active',
  },

  parseEstimateDocument: {
    id: 'parseEstimateDocument',
    name: 'Estimate Document Parser',
    product: 'insurance-estimator',
    currentVersion: 1,
    systemPrompt: 'You are an expert at parsing property repair and insurance estimates. Extract EVERY line item - do not filter or skip. Respond only with valid JSON.',
    userPromptTemplate: `You are an expert at reading insurance property damage estimates (Xactimate, Symbility, etc.).

YOUR MISSION: Extract EVERY SINGLE line item from this document. Do not skip anything. Do not merge anything.

STEP 1 - COUNT: First, count how many line items exist in this document. Remember this number.

STEP 2 - EXTRACT: For each line item:
- Capture the FULL description exactly as written
- Find the LINE TOTAL (rightmost dollar column, not unit rates or component costs)
- Preserve details like quantities, SF, LF, units in the description
- Categorize as "mitigation" or "repair" using your expertise

CATEGORIZATION (use your expertise):
- "mitigation": Water removal, drying, preventing further damage (equipment, extraction, monitoring, containment, etc.)
- "repair": Restoring the property (demolition, construction, finishing, cleaning, O&P, etc.)

CRITICAL:
- Do NOT filter based on item type - capture EVERYTHING including HVAC, appliances, fixtures, etc.
- Do NOT merge similar items - keep them separate
- Do NOT skip items that seem minor, unusual, or don't fit typical categories
- Your output MUST have the same number of line items as the source document

STEP 3 - VERIFY: Before responding, count your output line items. Does it match Step 1? If not, you missed items.

Document: {{fileName}}
Content:
{{documentText}}

Respond with JSON only (no markdown):
{
  "sourceLineItemCount": <number you counted in Step 1>,
  "lineItems": [
    {"line": 1, "description": "Full description with qty/SF/units if present", "amount": 123.45, "category": "repair"}
  ],
  "total": 1234.56,
  "mitigationTotal": 100.00,
  "repairTotal": 1134.56,
  "propertyAddress": "extracted address if found or null",
  "notes": "any observations about the document structure or parsing"
}`,
    templateVariables: [
      { name: 'fileName', type: 'string', description: 'Original file name' },
      { name: 'documentText', type: 'string', description: 'Extracted document text' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 8000,
    temperature: 0.7,
    outputFormat: 'json',
    jsonSchema: null,
    status: 'active',
  },

  processClarification: {
    id: 'processClarification',
    name: 'Clarification Processor',
    product: 'insurance-estimator',
    currentVersion: 1,
    systemPrompt: 'You are an insurance estimate specialist. You can adjust mitigation, repairs, or flag parsing issues. Respond only with valid JSON.',
    userPromptTemplate: `You are helping adjust an insurance estimate based on user clarification.

FULL ESTIMATE CONTEXT:
{{estimateContext}}

User clarification: "{{userMessage}}"

You can make changes to ANY part of the estimate:

1. UPLOAD/PARSING ISSUES - If user says items were miscategorized or amounts are wrong:
   - Suggest re-categorizing items (repair vs mitigation)
   - Suggest correcting amounts
   - Return changes like: {"field": "reparseUpload", "reason": "user indicated items miscategorized"}

2. MITIGATION CHANGES - For equipment/drying adjustments:
   - If SF changes: Dehus = CEILING(SF / 500), Air Movers = (Dehus * 2.5) + 2 rounded up
   - If user mentions weekend/after-hours, set afterHours to true
   - If user mentions rooms/areas, estimate SF (bedroom ~150SF, bathroom ~50SF, kitchen ~200SF, living room ~300SF)

3. REPAIR CHANGES - For repair line item adjustments:
   - Adjust estimatedAmount for specific items
   - Change trade classification
   - Adjust laborPct splits
   - Return changes like: {"field": "repairItem", "itemId": "repair-0", "subfield": "estimatedAmount", "to": 5000}

Respond with JSON only (no markdown):
{
  "message": "Your response explaining what you understood and changed",
  "changes": [
    {"field": "sqft", "from": 450, "to": 720},
    {"field": "repairItem", "itemId": "repair-0", "subfield": "estimatedAmount", "from": 1000, "to": 1500}
  ],
  "needsReparse": false,
  "followUpNeeded": false,
  "followUpQuestion": null
}

If the user indicates the original upload was parsed incorrectly, set needsReparse to true.
If you don't understand the clarification, set followUpNeeded to true and ask a clarifying question.`,
    templateVariables: [
      { name: 'estimateContext', type: 'object', description: 'Full estimate context as JSON' },
      { name: 'userMessage', type: 'string', description: 'User clarification message' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 1000,
    temperature: 0.7,
    outputFormat: 'json',
    jsonSchema: null,
    status: 'active',
  },

  generateEnhancedBriefing: {
    id: 'generateEnhancedBriefing',
    name: 'Enhanced WO Briefing with Intelligence',
    product: 'trades-app',
    currentVersion: 1,
    systemPrompt: 'You are an expert maintenance assistant with deep knowledge of property maintenance patterns. Be concise, practical, and proactive. Prioritize safety information first.',
    userPromptTemplate: `You are briefing a maintenance technician before they go to a job.

Current Work Order:
- ID: {{woId}}
- Issue: {{description}}
- Location: {{property}} Unit {{unit}}

Unit History:
{{historyText}}

Labor Notes:
{{notesText}}

=== TRIBAL KNOWLEDGE (tips from other techs who've worked this unit) ===
{{knowledgeNotes}}

=== PREDICTED PARTS (based on historical data for similar jobs) ===
{{predictedParts}}

=== FAILURE INTELLIGENCE ===
{{failureIntel}}

=== REPAIR CONFIDENCE (past fix quality at this unit) ===
{{repairConfidence}}

Provide an enhanced briefing:
1. SAFETY FIRST — Any safety notes from tribal knowledge
2. WHAT TO EXPECT — Summarize the issue + relevant unit quirks/access info
3. PATTERN ALERT — Is this a recurring issue? Have prior fixes failed?
4. RECOMMENDED APPROACH — Based on what worked (or didn't) before
5. PARTS TO BRING — Based on predictions and past usage
6. TENANT NOTES — Communication preferences, pets, schedule

Keep it concise and actionable. A tech should be able to read this in 60 seconds.`,
    templateVariables: [
      { name: 'woId', type: 'string', description: 'Work order ID' },
      { name: 'description', type: 'string', description: 'Issue description' },
      { name: 'property', type: 'string', description: 'Property name' },
      { name: 'unit', type: 'string', description: 'Unit number' },
      { name: 'historyText', type: 'string', description: 'Formatted unit history' },
      { name: 'notesText', type: 'string', description: 'Formatted labor notes' },
      { name: 'knowledgeNotes', type: 'string', description: 'Tribal knowledge notes formatted for prompt' },
      { name: 'predictedParts', type: 'string', description: 'Predicted parts list formatted for prompt' },
      { name: 'failureIntel', type: 'string', description: 'Recurring issues, failure probability, cross-property patterns' },
      { name: 'repairConfidence', type: 'string', description: 'Past repair quality scores for this unit' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 1500,
    temperature: 0.7,
    outputFormat: 'text',
    jsonSchema: null,
    status: 'active',
  },

  analyzePropertyHealth: {
    id: 'analyzePropertyHealth',
    name: 'Property Health Analyzer',
    product: 'trades-app',
    currentVersion: 1,
    systemPrompt: 'You are a property maintenance strategist. Analyze data to provide actionable insights. Respond only with valid JSON.',
    userPromptTemplate: `Analyze this property's maintenance health and provide strategic recommendations.

Property: {{propertyName}}
Health Score: {{healthScore}}/100

Metrics:
{{healthDetails}}

Recurring Issues:
{{recurringIssues}}

Cross-Property Patterns:
{{crossPatterns}}

Seasonal Trends:
{{seasonalData}}

Provide strategic analysis as JSON:
{
  "summary": "One-sentence health assessment",
  "topConcerns": ["Concern 1", "Concern 2"],
  "proactiveActions": [
    {"action": "What to do", "priority": "high|medium|low", "reason": "Why", "estimatedSavings": "Rough $ saved by preventing emergency"}
  ],
  "seasonalPrep": "What to prepare for based on upcoming season",
  "budgetAlert": "Any budget implications from patterns"
}`,
    templateVariables: [
      { name: 'propertyName', type: 'string', description: 'Property name' },
      { name: 'healthScore', type: 'number', description: 'Property health score 0-100' },
      { name: 'healthDetails', type: 'string', description: 'Health score breakdown' },
      { name: 'recurringIssues', type: 'string', description: 'Recurring issue patterns' },
      { name: 'crossPatterns', type: 'string', description: 'Cross-property patterns' },
      { name: 'seasonalData', type: 'string', description: 'Seasonal pattern data' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 1000,
    temperature: 0.7,
    outputFormat: 'json',
    jsonSchema: null,
    status: 'active',
  },

  classifyRepairItem: {
    id: 'classifyRepairItem',
    name: 'Repair Item Classifier',
    product: 'insurance-estimator',
    currentVersion: 1,
    systemPrompt: 'You are an expert at classifying construction and repair work. Respond only with valid JSON.',
    userPromptTemplate: `Classify this repair line item into a trade category and suggest a labor/material split percentage.

Line item: "{{description}}"
Amount: \${{amount}}

Available trades: demolition, drywall, texture, paint, flooringLvp, flooringCarpet, flooringTile, flooringHardwood, trim, casing, cabinets, plumbing, electrical, cleaning, general

Standard labor percentages by trade:
- demolition: 85%
- drywall: 55%
- texture: 70%
- paint: 65%
- flooringLvp: 40%
- flooringCarpet: 35%
- flooringTile: 50%
- plumbing: 70%
- electrical: 75%
- cleaning: 90%
- general: 50%

Respond with JSON only (no markdown):
{
  "trade": "drywall",
  "laborPct": 55,
  "confidence": 0.92,
  "reasoning": "Standard drywall removal and replacement",
  "shouldSplit": false,
  "splitItems": []
}

Set shouldSplit to true if this item should be broken into multiple line items (e.g., flooring should split into removal + installation + antimicrobial). Include splitItems array with suggested breakdown.`,
    templateVariables: [
      { name: 'description', type: 'string', description: 'Line item description' },
      { name: 'amount', type: 'number', description: 'Line item amount' },
    ],
    model: 'gpt-4o-mini',
    maxTokens: 500,
    temperature: 0.7,
    outputFormat: 'json',
    jsonSchema: null,
    status: 'active',
  },
};

/**
 * Get a prompt definition by ID
 * @param {string} promptId
 * @returns {Object|null}
 */
export const getPromptDefinition = (promptId) => PROMPT_DEFINITIONS[promptId] || null;

/**
 * Get all prompt definitions as an array (useful for seeding)
 * @returns {Array<Object>}
 */
export const getPromptDefinitionsArray = () => Object.values(PROMPT_DEFINITIONS);
